import Papa from "papaparse";
import type { ContactRowInputType } from "@/types/graphql/partner.generated";

// Заголовки колонок, по которым CSV узнаёт адрес, имя и язык (сравнение без
// учёта регистра и пробелов по краям).
const EMAIL_HEADERS = [
  "email",
  "e-mail",
  "mail",
  "почта",
  "электронная почта",
  "e-mail address",
  "email address",
];
const NAME_HEADERS = [
  "name",
  "имя",
  "full name",
  "fullname",
  "first name",
  "firstname",
];
const LANGUAGE_HEADERS = ["language", "lang", "язык", "locale"];

const norm = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase();
const hasAt = (value: unknown) => String(value ?? "").includes("@");
const cell = (value: unknown) => String(value ?? "").trim() || null;

/**
 * Разбор вставленного списка: адреса через запятую, точку с запятой, пробел
 * или с новой строки, можно «Имя <адрес>». Сначала именованные адреса,
 * потом прочие. Проверку и нормализацию адресов делает бэкенд, кабинет
 * только собирает строки.
 */
export function parseTextInput(text: string): ContactRowInputType[] {
  const rows: ContactRowInputType[] = [];
  const named = /([^,;\n<>]+?)\s*<([^<>\s]+@[^<>\s]+)>/g;
  const rest = text.replace(named, (_match, name: string, email: string) => {
    rows.push({ email: email.trim(), name: name.trim() });
    return " ";
  });
  for (const token of rest.split(/[\s,;]+/)) {
    if (token.includes("@")) rows.push({ email: token.trim() });
  }
  return rows;
}

/**
 * Разбор CSV: разделитель и кавычки определяет papaparse, BOM срезается.
 * Есть заголовок с колонкой адреса: берутся email, name и language по
 * заголовкам. Нет заголовка: первая колонка первой строки, похожая на
 * адрес, имя и язык не берутся.
 */
export function parseCsvInput(text: string): ContactRowInputType[] {
  const { data } = Papa.parse<string[]>(text.replace(/^\uFEFF/, ""), {
    skipEmptyLines: "greedy",
  });
  const first = data[0];
  if (!first) return [];
  const header = first.map(norm);
  const emailCol = header.findIndex((h) => EMAIL_HEADERS.includes(h));
  if (emailCol >= 0) {
    const nameCol = header.findIndex((h) => NAME_HEADERS.includes(h));
    const langCol = header.findIndex((h) => LANGUAGE_HEADERS.includes(h));
    return data
      .slice(1)
      .filter((row) => hasAt(row[emailCol]))
      .map((row) => ({
        email: String(row[emailCol]).trim(),
        name: nameCol >= 0 ? cell(row[nameCol]) : null,
        language: langCol >= 0 ? cell(row[langCol]) : null,
      }));
  }
  const col = first.findIndex(hasAt);
  if (col < 0) return [];
  return data
    .filter((row) => hasAt(row[col]))
    .map((row) => ({
      email: String(row[col]).trim(),
      name: null,
      language: null,
    }));
}
